import { useEffect, useMemo, useState } from 'react';
import {
  CATEGORIAS_DE_PRECIO, PRODUCTOS_BIMBO, faltaEnPrecio, leerPvp, productoDe,
} from '../domain/barridoPrecios';
import type { PrecioGondola } from '../domain/barridoPrecios';

/**
 * Precios en góndola, dentro del cierre de turno (pedido de Bimbo del 27-oct; decisión del usuario del 1-nov).
 *
 * Se agregan de a uno: elige un producto Bimbo de la lista (la categoría, la marca y la presentación se completan solas)
 * o «Otro producto u otra marca» y lo escribe. El precio siempre lo escribe ella. No es obligatorio: en varias tiendas
 * les quitan el celular al entrar y lo llenan de memoria.
 *
 * `onPendiente` avisa si dejó algo escrito sin tocar «Agregar»: el cierre no se envía con un precio a medias, porque se
 * perdería sin que ella lo sepa.
 */
export default function PreciosEnGondola({
  valor, onChange, onPendiente,
}: {
  valor: PrecioGondola[];
  onChange: (v: PrecioGondola[]) => void;
  onPendiente: (hay: boolean) => void;
}) {
  /** '' = nada elegido · 'otro' = lo escribe · un número = índice en PRODUCTOS_BIMBO. */
  const [eleccion, setEleccion] = useState('');
  const [categoria, setCategoria] = useState('');
  const [marca, setMarca] = useState('');
  const [sku, setSku] = useState('');
  const [pvp, setPvp] = useState('');
  const [aviso, setAviso] = useState('');

  const bimbo = eleccion !== '' && eleccion !== 'otro' ? PRODUCTOS_BIMBO[Number(eleccion)] : null;
  const linea = bimbo
    ? { categoria: bimbo.categoria, marca: 'Bimbo', sku: bimbo.sku, pvp }
    : { categoria, marca, sku, pvp };
  const pendiente = eleccion !== '' && (bimbo !== null || sku.trim() !== '' || pvp.trim() !== '');
  useEffect(() => { onPendiente(pendiente); }, [pendiente, onPendiente]);

  /** Las marcas que ya escribió, para no teclear «Unión» cinco veces. */
  const marcasUsadas = useMemo(
    () => [...new Set(['Bimbo', ...valor.map((p) => p.marca)])].sort((a, b) => a.localeCompare(b, 'es')),
    [valor],
  );

  function agregar() {
    const falta = faltaEnPrecio(linea);
    if (falta) { setAviso(falta); return; }
    onChange([...valor, {
      categoria: linea.categoria.trim(),
      marca: linea.marca.trim(),
      sku: linea.sku.trim(),
      producto: productoDe(linea.categoria, linea.marca, linea.sku),
      pvp: linea.pvp.trim(),
      sugerido: bimbo !== null,
    }]);
    // Se queda en «otro» con la categoría y la marca puestas: lo normal es anotar varios de la misma marca seguidos.
    if (bimbo) setEleccion('');
    setSku('');
    setPvp('');
    setAviso('');
  }

  const campo = 'min-h-[44px] w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm';
  const etiqueta = 'mb-1 block text-sm font-medium text-gray-700';

  return (
    <section className="mb-4 rounded-xl border border-gray-200 bg-white p-4">
      <h2 className="mb-1 text-base font-bold text-gray-900">Precios en góndola</h2>
      <p className="mb-3 text-xs text-gray-500">
        Bimbo nos pidió saber qué productos de pan blanco, pan especial, tortillas, pita, tostadas, pan molido y bollería
        hay en la tienda y a qué precio, de todas las marcas. Anota uno por uno los que recuerdes. No es obligatorio.
      </p>

      {valor.length > 0 && (
        <ul className="mb-3 divide-y divide-gray-100 rounded border border-gray-200">
          {valor.map((p, i) => (
            <li key={`${p.producto}-${i}`} className="flex items-center gap-2 px-3 py-2 text-sm">
              <span className="min-w-0 flex-1">
                <span className="block font-medium text-gray-900">{p.marca} · {p.sku}</span>
                <span className="block text-xs text-gray-500">{p.categoria}</span>
              </span>
              <span className="whitespace-nowrap font-semibold tabular-nums">S/ {(leerPvp(p.pvp) ?? 0).toFixed(2)}</span>
              <button
                type="button"
                onClick={() => onChange(valor.filter((_, j) => j !== i))}
                className="min-h-[36px] rounded px-2 text-xs font-semibold text-red-600"
              >
                Quitar
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="rounded-lg bg-gray-50 p-3">
        <label className={etiqueta} htmlFor="precio-que">
          {valor.length ? '¿Qué otro producto viste?' : '¿Qué producto viste?'}
        </label>
        <select
          id="precio-que" value={eleccion}
          onChange={(e) => { setEleccion(e.target.value); setAviso(''); }}
          className={`${campo} mb-3`}
        >
          <option value="">Elige un producto…</option>
          {CATEGORIAS_DE_PRECIO.map((c) => {
            const de = PRODUCTOS_BIMBO.map((p, i) => ({ ...p, i })).filter((p) => p.categoria === c);
            return de.length ? (
              <optgroup key={c} label={`${c} · Bimbo`}>
                {de.map((p) => <option key={p.i} value={String(p.i)}>Bimbo {p.sku}</option>)}
              </optgroup>
            ) : null;
          })}
          <option value="otro">Otro producto u otra marca (lo escribo)</option>
        </select>

        {eleccion === 'otro' && (
          <>
            <label className={etiqueta} htmlFor="precio-cat">Categoría</label>
            <select id="precio-cat" value={categoria} onChange={(e) => setCategoria(e.target.value)} className={`${campo} mb-3`}>
              <option value="">Elige…</option>
              {CATEGORIAS_DE_PRECIO.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <label className={etiqueta} htmlFor="precio-marca">Marca</label>
            <input
              id="precio-marca" type="text" list="precio-marcas" value={marca} placeholder="Unión, Bell's, Tottus…"
              onChange={(e) => setMarca(e.target.value)} className={`${campo} mb-3`}
            />
            <datalist id="precio-marcas">
              {marcasUsadas.map((m) => <option key={m} value={m} />)}
            </datalist>
            <label className={etiqueta} htmlFor="precio-sku">Producto y presentación</label>
            <input
              id="precio-sku" type="text" value={sku} placeholder="Semilla 500 g"
              onChange={(e) => setSku(e.target.value)} className={`${campo} mb-3`}
            />
          </>
        )}

        {eleccion !== '' && (
          <>
            <label className={etiqueta} htmlFor="precio-pvp">Precio en góndola (S/)</label>
            <input
              id="precio-pvp" type="text" inputMode="decimal" value={pvp} placeholder="13,50"
              onChange={(e) => setPvp(e.target.value)} className={`${campo} mb-3`}
            />
            {aviso && <p className="mb-2 text-sm text-red-600">{aviso}</p>}
            <button
              type="button" onClick={agregar}
              className="min-h-[44px] w-full rounded-lg border border-indigo-600 bg-white text-sm font-bold text-indigo-700"
            >
              Agregar a la lista
            </button>
          </>
        )}
      </div>
      {valor.length > 0 && (
        <p className="mt-2 text-xs text-gray-500">
          {valor.length === 1 ? 'Llevas 1 producto anotado.' : `Llevas ${valor.length} productos anotados.`}
        </p>
      )}
    </section>
  );
}
